import { computed, IReactionDisposer, observable, reaction, runInAction } from 'mobx';
import { inject, Observer, observer } from 'mobx-react';
import * as React from 'react';
import MovieRentalCatalogStore, { ContentType, MoviesByCategoryParams } from '../stores/movierental/MovieRentalCatalogStore';
import TvRentalCatalog from './TvRentalCatalog';

export interface MovieRentalCatalogProps {
  rootCategoryId: string;
  selectedCategoryId: string;
  rowsPerPage?: number;
  type?: ContentType;
  vodType?: string;
  packetIds?: string[];
  isFreeOfCharge?: boolean;
  specialPricePacketIds?: number[];
  movieRentalCatalogStore?: MovieRentalCatalogStore;
}

const MovieRentalCatalog = observer(
  class MovieRentalCatalog extends React.Component<MovieRentalCatalogProps> {
    private readonly moviesPerRow: number = 6;
    private moviesByCategoryParamsReactionDisposer: IReactionDisposer = null;

    @observable accessor rowsPerPage: number = 2;

    @observable accessor page: number = 1;
    @observable accessor isFreeOfCharge: boolean;
    @observable accessor specialPricePacketIds: number[];
    @observable accessor movieCategoryParams: MoviesByCategoryParams = {
      rootCategoryId: null,
      selectedCategoryId: null,
      type: null,
      vodType: null,
      packetIds: null,
      rowsPerPage: null,
      offset: null,
      limit: null,
      page: null,
      moviesPerPage: null
    };

    public componentDidMount() {
      const { movieRentalCatalogStore } = this.props;
      runInAction(() => {
        this.rowsPerPage = this.props.rowsPerPage || 2;
        this.isFreeOfCharge = this.props.isFreeOfCharge;
        this.specialPricePacketIds = this.props.specialPricePacketIds;
      });
      this.updateParams();
      this.moviesByCategoryParamsReactionDisposer = reaction(
        () => this.movieCategoryParams,
        () => movieRentalCatalogStore.loadMoviesByCategoryAndPage(this.movieCategoryParams),
        { fireImmediately: true }
      );
    }

    public componentWillUnmount() {
      this.moviesByCategoryParamsReactionDisposer();
    }

    public componentDidUpdate() {
      this.updateParams();
    }

    private onPageChange = (page: number) => {
      runInAction(() => {
        this.page = page;
        this.movieCategoryParams = this.getMoviesByCategoryParams();
      });
    };

    private updateParams = () => {
      runInAction(() => {
        this.movieCategoryParams = this.getMoviesByCategoryParams();
      });
    };

    getMoviesByCategoryParams = (): MoviesByCategoryParams => {
      const { type, vodType, packetIds, rootCategoryId, selectedCategoryId } = this.props;
      return {
        rootCategoryId,
        selectedCategoryId,
        type: type || ContentType.ASSETS,
        vodType,
        packetIds,
        rowsPerPage: this.rowsPerPage,
        offset: 0,
        limit: this.moviesPerPage,
        page: this.page,
        moviesPerPage: this.moviesPerPage
      };
    };

    @computed
    get moviesPerPage() {
      return this.moviesPerRow * this.rowsPerPage;
    }

    private loadMoviesForPage = (page: number) => {
      const params = { ...this.movieCategoryParams, offset: this.moviesPerPage * (page - 1), page };
      this.props.movieRentalCatalogStore.loadMoviesByCategoryAndPage(params);
    };

    public render() {
      const { movieRentalCatalogStore } = this.props;
      return (
        <Observer>
          {() => (
            <TvRentalCatalog
              movies={movieRentalCatalogStore.getMovies(this.movieCategoryParams)}
              loadMore={this.loadMoviesForPage}
              moviesPerPage={this.moviesPerPage}
              isFetching={movieRentalCatalogStore.isFetchingMovies(this.movieCategoryParams)}
              totalCount={movieRentalCatalogStore.getMoviesTotalCount(this.movieCategoryParams)}
              onPageChange={this.onPageChange}
              rootCategoryId={this.movieCategoryParams.rootCategoryId}
              isFreeOfCharge={this.isFreeOfCharge}
              specialPricePacketIds={this.specialPricePacketIds}
            />
          )}
        </Observer>
      );
    }
  }
);

export default inject('movieRentalCatalogStore')(MovieRentalCatalog);
